import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GooglePlaceField } from './GooglePlaceField';
import { X } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';

const ImageModal = ({ isOpen, onClose, imageUrl, altText }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] mx-4">
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
        <img
          src={imageUrl}
          alt={altText}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onClick={e => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

const FormField = ({ company, field, handleInputChange, error }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (field.defaultValue && !field.value) {
      handleInputChange(field.id, field.defaultValue);
    }
  }, [field.defaultValue, field.id, field.value, handleInputChange]);


  const handleSelectionChange = (fieldId, value, options) => {
    if (field.enableEditing === false) return;
    handleInputChange(fieldId, value);
  };

  const handleWheel = (e) => {
    e.target.blur();
  };

  const handleImageClick = (imageUrl, label) => {
    setSelectedImage({ url: imageUrl, alt: label });
  };  
  const renderImage = (imageUrl, label, option) => (
    <div className="w-full h-full relative flex items-center justify-center group">
      <div className="rounded-lg overflow-hidden h-full w-full flex items-center justify-center">
        <img
          src={`${process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL}${imageUrl}`}
          alt={label}
          className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-105"
        />
      </div>
      {option?.enableImageEnlarge===true && (
        <button
          onClick={(e) => 
            {
              e.preventDefault();
              e.stopPropagation(); // Prevent click from selecting the radio option
              handleImageClick(`${process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL}${imageUrl}`, label);
            }}
          className="absolute top-1 right-1 p-1.5 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-white transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m4-3H6" />
          </svg>
        </button>
      )}
    </div>
  );

  const validateEmail = useCallback((val) => {
    if (field.dataType !== 'Email') return;
    const value = (val || '').trim();
    if (!value) {
      if (field.isRequired) {
        setEmailError('Email is required');
      } else {
        setEmailError('');
      }
      return;
    }
    // Simple robust-enough email pattern for client-side validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  }, [field.dataType, field.isRequired]);

  // Early return AFTER hooks to satisfy React Hooks rules
  if (field.hidden) {
    return null;
  }

  const renderField = () => {
    const initialValue = field.value || field.defaultValue || '';
    const isDisabled = field.enableEditing === false;

    switch (field.dataType) {
      case 'GooglePlace':
        return (
          <GooglePlaceField
            company={company}
            field={{
              ...field,
              country: field.country,
              value: initialValue
            }}
            value={initialValue}
            onChange={(id, value) => handleInputChange(id, value)}
            error={error}
            disabled={isDisabled}
          />
        );
      case 'Text':
      case 'Email':
      case 'PhoneNumber':
        return (
          <input
            type={field.dataType === 'Email' ? 'email' : 'text'}
            id={field.id}
            value={initialValue}
            onChange={(e) => {
              const val = e.target.value;
              handleInputChange(field.id, val);
              if (field.dataType === 'Email') {
                // Live validation (soft). Only validate if user typed an @ or a dot to reduce noise.
                if (val.includes('@') || val.includes('.')) {
                  validateEmail(val);
                } else if (!val) {
                  validateEmail(val);
                }
              }
            }}
            onBlur={(e) => {
              if (field.dataType === 'Email') validateEmail(e.target.value);
            }}
            placeholder={field.placeHolderText}
            disabled={isDisabled}
            className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            aria-invalid={field.dataType === 'Email' && !!emailError}
            aria-describedby={field.dataType === 'Email' && emailError ? `${field.id}-email-error` : undefined}
          />
        );
      case 'TextArea':
        return (
          <textarea
            id={field.id}
            value={initialValue}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeHolderText}
            rows={field.noOfLines || 3}
            disabled={isDisabled}
            className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
        );      case 'CheckboxSelection':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {field.options.map((option, index) => {
              const isSelected = (initialValue || []).includes(option.label);
              return (
                <div 
                  key={index} 
                  onClick={() => {
                    if (!isDisabled) {
                      const updatedValues = isSelected
                        ? (field.value || []).filter(v => v !== option.label)
                        : [...(field.value || []), option.label];
                      handleSelectionChange(field.id, updatedValues, field.options);
                    }
                  }}
                  className={`flex flex-col border rounded-lg p-3 transition-all cursor-pointer relative
                    ${isDisabled ? 'opacity-70 cursor-not-allowed' : ''}
                    ${isSelected 
                      ? 'border-green-500 bg-green-50 shadow-md' 
                      : !isDisabled ? 'hover:border-green-300 hover:shadow-sm' : 'border-gray-200'}
                  `}
                >
                  {isSelected && (
                    <div className="absolute top-2 left-2 h-9 w-9 bg-green-500 rounded-full flex items-center justify-center z-10 shadow-md">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                  
                  {option.image ? (
                    <div className="flex-grow h-[220px] flex items-center justify-center mb-3">
                      {renderImage(option.image, option.label, option)}
                    </div>
                  ) : null}
                  
                  <div className={`flex flex-col items-center w-full ${option.image ? 'mt-auto' : ''}`}>
                    <span className={`text-sm font-medium text-center
                      ${isSelected ? 'text-green-700' : isDisabled ? 'text-gray-500' : 'text-gray-700'}
                    `}>
                      {option.label}
                    </span>
                    
                    {/* Hidden checkbox input for accessibility and form submission */}
                    <input
                      type="checkbox"
                      name={field.id}
                      value={option.label}
                      checked={isSelected}
                      onChange={(e) => {
                        const updatedValues = e.target.checked
                          ? [...(field.value || []), option.label]
                          : (field.value || []).filter(v => v !== option.label);
                        handleSelectionChange(field.id, updatedValues, field.options);
                      }}
                      disabled={isDisabled}
                      className="sr-only" // Visually hidden but still accessible
                      onClick={(e) => e.stopPropagation()} // Prevent clicking the input from triggering the parent click
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );      
        case 'RadioSelection':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {field.options.map((option, index) => {
              const isSelected = initialValue === option.label;
              return (
                <div 
                  key={index} 
                  onClick={() => {
                    if (!isDisabled) {
                      handleSelectionChange(field.id, option.label, field.options);
                    }
                  }}
                  className={`flex flex-col border rounded-lg p-3 transition-all cursor-pointer relative
                    ${isDisabled ? 'opacity-70 cursor-not-allowed' : ''}
                    ${isSelected 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : !isDisabled ? 'hover:border-blue-300 hover:shadow-sm' : 'border-gray-200'}
                  `}
                >
                  {isSelected && (
                    <div className="absolute top-2 left-2 h-9 w-9 bg-blue-500 rounded-full flex items-center justify-center z-10 shadow-md">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                  
                  {option.image ? (
                    <div className="flex-grow h-[220px] flex items-center justify-center mb-3">
                      {renderImage(option.image, option.label, option)}
                    </div>
                  ) : null}
                  
                  <div className={`flex flex-col items-center w-full ${option.image ? 'mt-auto' : ''}`}>
                    <span className={`text-sm font-medium text-center
                      ${isSelected ? 'text-blue-700' : isDisabled ? 'text-gray-500' : 'text-gray-700'}
                    `}>
                      {option.label}
                    </span>
                    
                    {/* Hidden radio input for accessibility and form submission */}
                    <input
                      type="radio"
                      name={field.id}
                      value={option.label}
                      checked={isSelected}
                      onChange={(e) => {
                        handleSelectionChange(field.id, e.target.value, field.options);
                      }}
                      disabled={isDisabled}
                      className="sr-only" // Visually hidden but still accessible
                      onClick={(e) => e.stopPropagation()} // Prevent clicking the input from triggering the parent click
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );case 'Date':
      case 'DateTime':
        return field.dataType === 'Date' ? (
          <DatePicker
            selected={initialValue ? new Date(initialValue) : null}
            onChange={(date) => {
              if (date) {
                const formattedDate = format(date, 'yyyy-MM-dd');
                handleInputChange(field.id, formattedDate);
              } else {
                handleInputChange(field.id, '');
              }
            }}
            dateFormat="yyyy-MM-dd"
            minDate={new Date()} // Only allow today or future dates
            disabled={isDisabled}
            className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
        ) : (
          <input
            type="datetime-local"
            id={field.id}
            value={initialValue}
            min={new Date().toISOString().slice(0, 16)} // Set minimum to current datetime
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            disabled={isDisabled}
            className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
        );
      case 'DaysSetter':
        const days = parseInt(initialValue) || 2; // Default to 2 days instead of 1
        // Calculate the breakdown
        const months = Math.floor(days / 30);
        const remainingDaysAfterMonths = days % 30;
        const weeks = Math.floor(remainingDaysAfterMonths / 7);
        const remainingDays = remainingDaysAfterMonths % 7;

        const updateDays = (newDays, newWeeks, newMonths) => {
          // Ensure values are not negative
          const adjustedDays = Math.max(0, newDays);
          const adjustedWeeks = Math.max(0, newWeeks);
          const adjustedMonths = Math.max(0, newMonths);
          
          // Calculate total days
          const totalDays = adjustedDays + (adjustedWeeks * 7) + (adjustedMonths * 30);
          handleInputChange(field.id, totalDays.toString());
        };
        if(initialValue === '' || initialValue === '0'){
          updateDays(2, 0, 0); // Initialize with 2 days
        } 
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Days */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex flex-col items-center">
                  <div className="flex items-center w-full justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => updateDays(remainingDays - 1, weeks, months)}
                      disabled={isDisabled || remainingDays === 0}
                      className={`px-4 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        isDisabled || remainingDays === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 hover:shadow'
                      }`}
                    >
                      -
                    </button>
                    <span className="text-2xl font-semibold text-gray-800">{remainingDays}</span>
                    <button
                      type="button"
                      onClick={() => updateDays(remainingDays + 1, weeks, months)}
                      disabled={isDisabled}
                      className={`px-4 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        isDisabled 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 hover:shadow'
                      }`}
                    >
                      +
                    </button>
                  </div>
                  <span className="text-base font-medium text-gray-700">Days</span>
                </div>
              </div>
              
              {/* Weeks */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex flex-col items-center">
                  <div className="flex items-center w-full justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => updateDays(remainingDays, weeks - 1, months)}
                      disabled={isDisabled || weeks === 0}
                      className={`px-4 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        isDisabled || weeks === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 hover:shadow'
                      }`}
                    >
                      -
                    </button>
                    <span className="text-2xl font-semibold text-gray-800">{weeks}</span>
                    <button
                      type="button"
                      onClick={() => updateDays(remainingDays, weeks + 1, months)}
                      disabled={isDisabled}
                      className={`px-4 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        isDisabled 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 hover:shadow'
                      }`}
                    >
                      +
                    </button>
                  </div>
                  <span className="text-base font-medium text-gray-700">Weeks</span>
                </div>
              </div>
              
              {/* Months */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex flex-col items-center">
                  <div className="flex items-center w-full justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => updateDays(remainingDays, weeks, months - 1)}
                      disabled={isDisabled || months === 0}
                      className={`px-4 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        isDisabled || months === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 hover:shadow'
                      }`}
                    >
                      -
                    </button>
                    <span className="text-2xl font-semibold text-gray-800">{months}</span>
                    <button
                      type="button"
                      onClick={() => updateDays(remainingDays, weeks, months + 1)}
                      disabled={isDisabled}
                      className={`px-4 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        isDisabled 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 hover:shadow'
                      }`}
                    >
                      +
                    </button>
                  </div>
                  <span className="text-base font-medium text-gray-700">Months</span>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 shadow-sm">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-700">Total days:</span>
                <span className="text-xl font-semibold text-blue-700">{days}</span>
              </div>
            </div>
          </div>
        );
      case 'Int':
      case 'Double':
        return field.dataType === 'Int' && field.showIncrementDecrement ? (
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => {
                if (isDisabled) return;
                const newValue = Number(initialValue || 0) - 1;
                handleInputChange(field.id, newValue.toString());
              }}
              disabled={isDisabled}
              className={`px-6 py-2 rounded-l-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors w-20 ${
                isDisabled 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                : 'bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-600'
              }`}
            >
              -
            </button>
            <input
              type="number"
              id={field.id}
              value={initialValue}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              step={field.dataType === 'Double' ? 'any' : '1'}
              placeholder={field.placeHolderText}
              onWheel={handleWheel}
              disabled={isDisabled}
              className={`w-full p-2 border-y focus:outline-none focus:ring-2 focus:ring-blue-500 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                isDisabled 
                ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                : 'border-blue-200'
              }`}
            />
            <button
              type="button"
              onClick={() => {
                if (isDisabled) return;
                const newValue = Number(initialValue || 0) + 1;
                handleInputChange(field.id, newValue.toString());
              }}
              disabled={isDisabled}
              className={`px-6 py-2 rounded-r-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors w-20 ${
                isDisabled 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                : 'bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-600'
              }`}
            >
              +
            </button>
          </div>
        ) : (
          <input
            type="number"
            id={field.id}
            value={initialValue}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            step={field.dataType === 'Double' ? 'any' : '1'}
            placeholder={field.placeHolderText}
            onWheel={handleWheel}
            disabled={isDisabled}
            className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="mb-6">
        <label htmlFor={field.id} className={`block text-sm font-medium mb-1 ${field.enableEditing === false ? 'text-gray-500' : 'text-gray-700'}`}>
          {field.label}
          {field.isRequired && <span className="text-red-500">*</span>}
        </label>
        {renderField()}
        {field.description && (
          <p className={`mt-1 text-sm ${field.enableEditing === false ? 'text-gray-400' : 'text-gray-500'}`}>{field.description}</p>
        )}
        {error && (
          <p className="mt-1 text-sm text-red-500">{error}</p>
        )}
        {!error && emailError && field.dataType === 'Email' && (
          <p id={`${field.id}-email-error`} className="mt-1 text-sm text-red-500">{emailError}</p>
        )}
      </div>
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.url}
        altText={selectedImage?.alt}
      />
    </>
  );
};

FormField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    dataType: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      image: PropTypes.string,
      properties: PropTypes.array
    })),
    value: PropTypes.any,
    defaultValue: PropTypes.any,
    showIncrementDecrement: PropTypes.bool,
    enableEditing: PropTypes.bool,
    hidden: PropTypes.bool
  }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  company: PropTypes.object.isRequired
};

export default FormField;